import React from "react";
import {useSelector} from "react-redux";
import {css, Theme} from "@emotion/react";

import {display, ml} from "@pg-design/helpers-css";
import {
    AdditionalAreaIcon,
    AreaIcon,
    BookmarkIcon,
    CalendarCheckIcon,
    FloorsIcon,
    FloorUpIcon,
    InfoIcon,
    KitchenIcon,
    PriceIcon,
    RoomsIcon,
    WorldSideIcon
} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {capitalize} from "@pg-mono/nodash";
import {identity, isEmpty} from "@pg-mono/nodash";
import {areaFormat, pluralize, priceFormat} from "@pg-mono/string-utils";

import {IRPStore} from "../../../app/rp_reducer";
import {useConstructionEndDate} from "../../../property/hooks/useConstructionEndDate";
import {IAdditionalArea} from "../../../property/interfaces/IAdditionalArea";
import {IPropertyListProperty} from "../../../property/types/PropertyListProperty";
import {getCurrency} from "../../../utils/get_currency";
import {PictogramNameType} from "../../detail/constants/quickview_modal_type";
import {OfferType} from "../../types/OfferType";

interface IProps {
    selectedProperty: IPropertyListProperty;
    className?: string;
}

interface IPictogramsConfiguration {
    label?: PictogramNameType;
    icon?: React.ReactNode;
    text?: string;
    id?: number;
    shouldShowPictogram: boolean;
}

const ICON_SIZE = "2.5";

export const QWPictograms = (props: IProps) => {
    const {selectedProperty, className} = props;
    const variables = useSelector((store: IRPStore) => store.variables.data);
    const roomsPlural = pluralize(["pokój", "pokoje", "pokoi"]);
    const floorsPlural = pluralize(["poziom", "poziomy", "poziomy"]);

    const setFloorText = (floor: number | null): string => {
        if (floor === null) {
            return "";
        } else if (floor === 0) {
            return "Parter";
        } else {
            return `${floor} piętro`;
        }
    };

    const additionalAreaList = selectedProperty.additional_areas.reduce((acc: IAdditionalArea[], aA: IAdditionalArea): IAdditionalArea[] => {
        const additionalAreaTypes = variables && variables.property_additional_areas;
        const foundAdditionalAreas = additionalAreaTypes?.find((aAType) => aAType.value === aA.type);
        return foundAdditionalAreas
            ? ([
                  ...acc,
                  {
                      area: aA.area,
                      type: foundAdditionalAreas.label
                  }
              ] as IAdditionalArea[])
            : acc;
    }, []);

    const additionalAreaListElement = () => additionalAreaList.map((item) => item.type.toString()).join(", ");

    // Kitchen
    const foundKitchenType = variables?.property_kitchen_type?.find((kT) => kT.value === selectedProperty.kitchen_type);
    const kitchenType: string = (foundKitchenType && foundKitchenType.label) || "";

    // Quarters
    const foundQuarterTypes = selectedProperty.quarters.map((q) => variables?.property_quarters?.find((qT) => qT.value === q));
    const quarterTypeList = foundQuarterTypes.map((item) => (item && item.label.toLowerCase()) || "").filter(identity) as string[];
    const listQuarterTypesElements = quarterTypeList.map((qT) => `${qT}`);
    const {calculateConstructionEndDate} = useConstructionEndDate();
    const constructionEndDate = calculateConstructionEndDate(selectedProperty.construction_end_date, selectedProperty.offer.construction_date_range);

    // Set pictogram data
    const setPictogram = (shouldRender: boolean, label: PictogramNameType, id: number): IPictogramsConfiguration => {
        //If not available set basic data for eventual debugging
        if (!shouldRender) {
            return {shouldShowPictogram: false, label};
        }

        const basicData = {
            label,
            id,
            shouldShowPictogram: true
        };

        switch (label) {
            case PictogramNameType.Price:
                return {
                    ...basicData,
                    icon: <PriceIcon size={ICON_SIZE} />,
                    text: priceFormat(selectedProperty.price as number, {unit: getCurrency({currency: selectedProperty.offer.currency})})
                };
            case PictogramNameType.ConstructionEndDate:
                return {
                    ...basicData,
                    icon: <CalendarCheckIcon size={ICON_SIZE} />,
                    text: constructionEndDate
                };
            case PictogramNameType.Rooms:
                return {
                    ...basicData,
                    icon: <RoomsIcon size={ICON_SIZE} />,
                    text: `${selectedProperty.rooms} ${roomsPlural(selectedProperty.rooms)}`
                };
            case PictogramNameType.Area:
                return {
                    ...basicData,
                    icon: <AreaIcon size={ICON_SIZE} />,
                    text: areaFormat(selectedProperty.area, {precision: 2})
                };
            case PictogramNameType.Floor:
                return {
                    ...basicData,
                    icon: <FloorUpIcon size={ICON_SIZE} />,
                    text: setFloorText(selectedProperty.floor)
                };
            case PictogramNameType.WorldSide:
                return {
                    ...basicData,
                    icon: <WorldSideIcon size={ICON_SIZE} />,
                    text: listQuarterTypesElements.join(", ")
                };
            case PictogramNameType.AdditionalArea:
                return {
                    ...basicData,
                    icon: <AdditionalAreaIcon size={ICON_SIZE} />,
                    text: additionalAreaListElement()
                };
            case PictogramNameType.Kitchen:
                return {
                    ...basicData,
                    icon: <KitchenIcon size={ICON_SIZE} />,
                    text: `${capitalize(kitchenType.toLowerCase())} kuchnia`
                };
            case PictogramNameType.Floors:
                return {
                    ...basicData,
                    icon: <FloorsIcon size={ICON_SIZE} />,
                    text: `${selectedProperty.floors} ${floorsPlural(selectedProperty.floors)}${selectedProperty.has_attic ? " w tym poddasze" : ""}`
                };
            case PictogramNameType.OfferNumber:
                return {
                    ...basicData,
                    icon: <BookmarkIcon size={ICON_SIZE} />,
                    text: selectedProperty.number
                };
            default:
                return {
                    ...basicData,
                    icon: <InfoIcon size={ICON_SIZE} />,
                    text: "Unknown data"
                };
        }
    };

    const pictogramsConfiguration: IPictogramsConfiguration[] = [
        setPictogram(selectedProperty.price !== null, PictogramNameType.Price, 1),
        setPictogram(!!constructionEndDate, PictogramNameType.ConstructionEndDate, 2),
        setPictogram(!!selectedProperty.rooms, PictogramNameType.Rooms, 3),
        setPictogram(!!selectedProperty.area, PictogramNameType.Area, 4),
        setPictogram(selectedProperty.floor >= 0 && selectedProperty.offer.type !== OfferType.HOUSE, PictogramNameType.Floor, 5),
        setPictogram(!isEmpty(selectedProperty.quarters), PictogramNameType.WorldSide, 6),
        setPictogram(!isEmpty(additionalAreaList), PictogramNameType.AdditionalArea, 7),
        setPictogram(!!kitchenType, PictogramNameType.Kitchen, 8),
        setPictogram(selectedProperty.floor !== null, PictogramNameType.Floors, 10),
        setPictogram(selectedProperty.number !== null, PictogramNameType.OfferNumber, 11)
    ];

    return (
        <div css={pictogramHolder} className={className}>
            {pictogramsConfiguration.map((pictogram) => {
                if (pictogram.shouldShowPictogram) {
                    return (
                        <div key={pictogram.id} css={dataHolder}>
                            {pictogram.icon}

                            <div css={ml(2)}>
                                <Text variant="body_copy_2" as="span" css={[labelColor, display("block")]}>
                                    {pictogram.label}
                                </Text>
                                <Text variant="body_copy_2" as="span" css={[display("block")]} strong>
                                    {pictogram.text}
                                </Text>
                            </div>
                        </div>
                    );
                }
            })}
        </div>
    );
};

const pictogramHolder = css`
    display: flex;
    flex-wrap: wrap;
    max-width: 55rem;
    padding-left: 0.5rem;
`;

const dataHolder = (theme: Theme) => css`
    width: 50%;
    padding-bottom: 3.2rem;
    display: flex;
    align-items: center;

    @media screen and (min-width: ${theme.breakpoints.sm}) {
        width: 32%;
    }
`;

const labelColor = (theme: Theme) => css`
    color: ${theme.colors.gray[700]};
`;
import React from "react";
import {useSelector} from "react-redux";
import {css, Theme} from "@emotion/react";

import {display, ml} from "@pg-design/helpers-css";
import {
    AdditionalAreaIcon,
    AreaIcon,
    BookmarkIcon,
    CalendarCheckIcon,
    FloorsIcon,
    FloorUpIcon,
    InfoIcon,
    KitchenIcon,
    PriceIcon,
    RoomsIcon,
    WorldSideIcon
} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {capitalize} from "@pg-mono/nodash";
import {identity, isEmpty} from "@pg-mono/nodash";
import {areaFormat, pluralize, priceFormat} from "@pg-mono/string-utils";

import {IRPStore} from "../../../app/rp_reducer";
import {useConstructionEndDate} from "../../../property/hooks/useConstructionEndDate";
import {IAdditionalArea} from "../../../property/interfaces/IAdditionalArea";
import {IPropertyListProperty} from "../../../property/types/PropertyListProperty";
import {getCurrency} from "../../../utils/get_currency";
import {PictogramNameType} from "../../detail/constants/quickview_modal_type";
import {OfferType} from "../../types/OfferType";

interface IProps {
    selectedProperty: IPropertyListProperty;
    className?: string;
}

interface IPictogramsConfiguration {
    label?: PictogramNameType;
    icon?: React.ReactNode;
    text?: string;
    id?: number;
    shouldShowPictogram: boolean;
}

const ICON_SIZE = "2.5";

export const QWPictograms = (props: IProps) => {
    const {selectedProperty, className} = props;
    const variables = useSelector((store: IRPStore) => store.variables.data);
    const roomsPlural = pluralize(["pokój", "pokoje", "pokoi"]);
    const floorsPlural = pluralize(["poziom", "poziomy", "poziomy"]);

    const setFloorText = (floor: number | null): string => {
        if (floor === null) {
            return "";
        } else if (floor === 0) {
            return "Parter";
        } else {
            return `${floor} piętro`;
        }
    };

    const additionalAreaList = selectedProperty.additional_areas.reduce((acc: IAdditionalArea[], aA: IAdditionalArea): IAdditionalArea[] => {
        const additionalAreaTypes = variables && variables.property_additional_areas;
        const foundAdditionalAreas = additionalAreaTypes?.find((aAType) => aAType.value === aA.type);
        return foundAdditionalAreas
            ? ([
                  ...acc,
                  {
                      area: aA.area,
                      type: foundAdditionalAreas.label
                  }
              ] as IAdditionalArea[])
            : acc;
    }, []);

    const additionalAreaListElement = () => additionalAreaList.map((item) => item.type.toString()).join(", ");

    // Kitchen
    const foundKitchenType = variables?.property_kitchen_type?.find((kT) => kT.value === selectedProperty.kitchen_type);
    const kitchenType: string = (foundKitchenType && foundKitchenType.label) || "";

    // Quarters
    const foundQuarterTypes = selectedProperty.quarters.map((q) => variables?.property_quarters?.find((qT) => qT.value === q));
    const quarterTypeList = foundQuarterTypes.map((item) => (item && item.label.toLowerCase()) || "").filter(identity) as string[];
    const listQuarterTypesElements = quarterTypeList.map((qT) => `${qT}`);
    const {calculateConstructionEndDate} = useConstructionEndDate();
    const constructionEndDate = calculateConstructionEndDate(selectedProperty.construction_end_date, selectedProperty.offer.construction_date_range);

    // Set pictogram data
    const setPictogram = (shouldRender: boolean, label: PictogramNameType, id: number): IPictogramsConfiguration => {
        //If not available set basic data for eventual debugging
        if (!shouldRender) {
            return {shouldShowPictogram: false, label};
        }

        const basicData = {
            label,
            id,
            shouldShowPictogram: true
        };

        switch (label) {
            case PictogramNameType.Price:
                return {
                    ...basicData,
                    icon: <PriceIcon size={ICON_SIZE} />,
                    text: priceFormat(selectedProperty.price as number, {unit: getCurrency({currency: selectedProperty.offer.currency})})
                };
            case PictogramNameType.ConstructionEndDate:
                return {
                    ...basicData,
                    icon: <CalendarCheckIcon size={ICON_SIZE} />,
                    text: constructionEndDate
                };
            case PictogramNameType.Rooms:
                return {
                    ...basicData,
                    icon: <RoomsIcon size={ICON_SIZE} />,
                    text: `${selectedProperty.rooms} ${roomsPlural(selectedProperty.rooms)}`
                };
            case PictogramNameType.Area:
                return {
                    ...basicData,
                    icon: <AreaIcon size={ICON_SIZE} />,
                    text: areaFormat(selectedProperty.area, {precision: 2})
                };
            case PictogramNameType.Floor:
                return {
                    ...basicData,
                    icon: <FloorUpIcon size={ICON_SIZE} />,
                    text: setFloorText(selectedProperty.floor)
                };
            case PictogramNameType.WorldSide:
                return {
                    ...basicData,
                    icon: <WorldSideIcon size={ICON_SIZE} />,
                    text: listQuarterTypesElements.join(", ")
                };
            case PictogramNameType.AdditionalArea:
                return {
                    ...basicData,
                    icon: <AdditionalAreaIcon size={ICON_SIZE} />,
                    text: additionalAreaListElement()
                };
            case PictogramNameType.Kitchen:
                return {
                    ...basicData,
                    icon: <KitchenIcon size={ICON_SIZE} />,
                    text: `${capitalize(kitchenType.toLowerCase())} kuchnia`
                };
            case PictogramNameType.Floors:
                return {
                    ...basicData,
                    icon: <FloorsIcon size={ICON_SIZE} />,
                    text: `${selectedProperty.floors} ${floorsPlural(selectedProperty.floors)}${selectedProperty.has_attic ? " w tym poddasze" : ""}`
                };
            case PictogramNameType.OfferNumber:
                return {
                    ...basicData,
                    icon: <BookmarkIcon size={ICON_SIZE} />,
                    text: selectedProperty.number
                };
            default:
                return {
                    ...basicData,
                    icon: <InfoIcon size={ICON_SIZE} />,
                    text: "Unknown data"
                };
        }
    };

    const pictogramsConfiguration: IPictogramsConfiguration[] = [
        setPictogram(selectedProperty.price !== null, PictogramNameType.Price, 1),
        setPictogram(!!constructionEndDate, PictogramNameType.ConstructionEndDate, 2),
        setPictogram(!!selectedProperty.rooms, PictogramNameType.Rooms, 3),
        setPictogram(!!selectedProperty.area, PictogramNameType.Area, 4),
        setPictogram(selectedProperty.floor >= 0 && selectedProperty.offer.type !== OfferType.HOUSE, PictogramNameType.Floor, 5),
        setPictogram(!isEmpty(selectedProperty.quarters), PictogramNameType.WorldSide, 6),
        setPictogram(!isEmpty(additionalAreaList), PictogramNameType.AdditionalArea, 7),
        setPictogram(!!kitchenType, PictogramNameType.Kitchen, 8),
        setPictogram(selectedProperty.floor !== null, PictogramNameType.Floors, 10),
        setPictogram(selectedProperty.number !== null, PictogramNameType.OfferNumber, 11)
    ];

    return (
        <div css={pictogramHolder} className={className}>
            {pictogramsConfiguration.map((pictogram) => {
                if (pictogram.shouldShowPictogram) {
                    return (
                        <div key={pictogram.id} css={dataHolder}>
                            {pictogram.icon}

                            <div css={ml(2)}>
                                <Text variant="body_copy_2" as="span" css={[labelColor, display("block")]}>
                                    {pictogram.label}
                                </Text>
                                <Text variant="body_copy_2" as="span" css={[display("block")]} strong>
                                    {pictogram.text}
                                </Text>
                            </div>
                        </div>
                    );
                }
            })}
        </div>
    );
};

const pictogramHolder = css`
    display: flex;
    flex-wrap: wrap;
    max-width: 55rem;
    padding-left: 0.5rem;
`;

const dataHolder = (theme: Theme) => css`
    width: 50%;
    padding-bottom: 3.2rem;
    display: flex;
    align-items: center;

    @media screen and (min-width: ${theme.breakpoints.sm}) {
        width: 32%;
    }
`;

const labelColor = (theme: Theme) => css`
    color: ${theme.colors.gray[700]};
`;
